import type { LucideIcon } from 'lucide-react'
import { MoreHorizontal } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '~/shared/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/ui/dropdown-menu'

interface CardAction {
  label: string
  icon: LucideIcon
  onSelect: () => void
  destructive?: boolean
  disabled?: boolean
}

/** Grace period for crossing the gap between trigger and menu without it closing. */
const CLOSE_DELAY_MS = 120

/**
 * Card overflow menu that opens on hover and stays reachable by click, so it
 * works on touch too. Place inside an element carrying `group`; the trigger
 * stays invisible until that group is hovered.
 */
export function CardActionsMenu({ label, actions }: { label: string; actions: CardAction[] }) {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<number | null>(null)

  const cancelClose = () => {
    if (closeTimer.current === null) return
    clearTimeout(closeTimer.current)
    closeTimer.current = null
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS)
  }

  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current)
    }
  }, [])

  const hoverProps = {
    onPointerEnter: (event: React.PointerEvent) => {
      if (event.pointerType !== 'mouse') return
      cancelClose()
      setOpen(true)
    },
    onPointerLeave: (event: React.PointerEvent) => {
      if (event.pointerType !== 'mouse') return
      scheduleClose()
    },
  }

  return (
    // Not modal: a modal menu locks pointer events on the body, which would swallow
    // the pointerleave that closes it again.
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger
        aria-label={label}
        className={cn(
          // Sits on the cover image, so it carries its own scrim rather than relying
          // on whatever the picture happens to show underneath.
          'inline-flex size-8 items-center justify-center rounded-full bg-black/60 text-white opacity-0 outline-none backdrop-blur-sm transition-opacity',
          'focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring hover:bg-black/80 group-hover:opacity-100 data-[state=open]:opacity-100',
          // Touch has no hover, so the affordance has to be permanent there.
          'pointer-coarse:opacity-100',
        )}
        {...hoverProps}
      >
        <MoreHorizontal className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="bottom" {...hoverProps}>
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            onSelect={action.onSelect}
            disabled={action.disabled}
            variant={action.destructive ? 'destructive' : 'default'}
          >
            <action.icon className="size-4" />
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
